import * as React from "react";
import { observer } from "mobx-react-lite";
import useRestricted from "../hooks/useRestricted";
import { Permission } from "../services/UserService";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import Logo from "../components/common/Logo";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { AddOutlined, BarChart, CalendarMonth, FormatListBulleted, KeyboardArrowDown } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import posthog from "posthog-js";
import { AppContext } from "../contexts/AppContext";
import TrainingRunItem from "../components/trainingRuns/TrainingRunItem";
import TrainingRunsChart from "../components/trainingRuns/TrainingRunsChart";

type DateRangeOption = "lastMonth" | "last3Months" | "last6Months" | "custom";

const NutritionalTraining: React.FC = observer(() => {
  const permission = useRestricted();

  const intl = useIntl();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (permission === Permission.free) {
      navigate('/');
    }
  }, [permission, navigate]);
  const theme = useTheme();
  const isUpMD = useMediaQuery(theme.breakpoints.up('md'));
  const { userService, trainingRunsService } = React.useContext(AppContext);

  const [dateRangeOption, setDateRangeOption] = React.useState<DateRangeOption>("lastMonth");
  const [mobileView, setMobileView] = React.useState<'list' | 'chart'>('list');
  const [customStartDate, setCustomStartDate] = React.useState<dayjs.Dayjs | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<dayjs.Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [customDialogOpen, setCustomDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (!userService.user?.id) {
      return;
    }
    trainingRunsService.clearCurrentTrainingRun();

    // Calculate date range
    const now = dayjs();
    let startDate: Date;
    let endDate: Date = now.endOf('day').toDate();

    switch (dateRangeOption) {
      case "lastMonth":
        startDate = now.subtract(1, 'month').startOf('day').toDate();
        break;
      case "last3Months":
        startDate = now.subtract(3, 'month').startOf('day').toDate();
        break;
      case "last6Months":
        startDate = now.subtract(6, 'month').startOf('day').toDate();
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = customStartDate.startOf('day').toDate();
          endDate = customEndDate.endOf('day').toDate();
        } else {
          return;
        }
        break;
      default:
        startDate = now.subtract(1, 'month').startOf('day').toDate();
    }

    trainingRunsService.loadTrainingRuns(userService.user.id, startDate, endDate);
  }, [trainingRunsService, userService.user?.id, dateRangeOption, customStartDate, customEndDate]);

  const handleAddTrainingRun = () => {
    posthog.capture('nutritional_training_add_run_clicked');
    navigate('/nutritional-training/add');
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDateRangeSelect = (option: DateRangeOption) => {
    if (option === "custom") {
      setCustomDialogOpen(true);
      handleMenuClose();
    } else {
      setDateRangeOption(option);
      handleMenuClose();
      posthog.capture('date_range_selected', { option });
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      // Validate that range is not longer than 6 months
      const monthsDiff = customEndDate.diff(customStartDate, 'month', true);
      if (monthsDiff > 6) {
        // Range too long, don't apply
        return;
      }
      setDateRangeOption("custom");
      setCustomDialogOpen(false);
      posthog.capture('custom_date_range_applied', {
        startDate: customStartDate.format('YYYY-MM-DD'),
        endDate: customEndDate.format('YYYY-MM-DD')
      });
    }
  };

  const getDateRangeLabel = () => {
    switch (dateRangeOption) {
      case "lastMonth":
        return intl.formatMessage({ id: "nutritionalTraining.lastMonth" });
      case "last3Months":
        return intl.formatMessage({ id: "nutritionalTraining.last3Months" });
      case "last6Months":
        return intl.formatMessage({ id: "nutritionalTraining.last6Months" });
      case "custom":
        if (customStartDate && customEndDate) {
          return `${customStartDate.format('ll')} - ${customEndDate.format('ll')}`;
        }
        return intl.formatMessage({ id: "nutritionalTraining.custom" });
    }
  };

  return (
    <>
      <Box paddingX={4} paddingTop={4} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">{intl.formatMessage({ id: "nav.nutritionalTraining" })}</Typography>
        {isUpMD && (
          <Box position="absolute" left="50%" sx={{ transform: 'translateX(calc(-50% + 32px))' }}>
            <Logo width={100} />
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddTrainingRun}
          startIcon={<AddOutlined />}
        >
          {intl.formatMessage({ id: "nutritionalTraining.addTrainingRun" })}
        </Button>
      </Box>

      <Box paddingX={4} paddingTop={2}>
        <Button
          variant="contained"
          onClick={handleMenuClick}
          startIcon={<CalendarMonth />}
          endIcon={<KeyboardArrowDown />}
          sx={{ mb: 2 }}
        >
          {getDateRangeLabel()}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleDateRangeSelect("lastMonth")}>
            {intl.formatMessage({ id: "nutritionalTraining.lastMonth" })}
          </MenuItem>
          <MenuItem onClick={() => handleDateRangeSelect("last3Months")}>
            {intl.formatMessage({ id: "nutritionalTraining.last3Months" })}
          </MenuItem>
          <MenuItem onClick={() => handleDateRangeSelect("last6Months")}>
            {intl.formatMessage({ id: "nutritionalTraining.last6Months" })}
          </MenuItem>
          <MenuItem onClick={() => handleDateRangeSelect("custom")}>
            {intl.formatMessage({ id: "nutritionalTraining.custom" })}
          </MenuItem>
        </Menu>

        {trainingRunsService.isLoadingTrainingRuns ? (
          <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        ) : trainingRunsService.trainingRuns.length === 0 ? (
          <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Typography variant="body1" color="text.secondary">
              {intl.formatMessage({ id: "nutritionalTraining.noTrainingRunsInPeriod" })}
            </Typography>
          </Box>
        ) : (
          <>
            {!isUpMD && (
              <Box display="flex" justifyContent="center" mb={2}>
                <ToggleButtonGroup
                  value={mobileView}
                  exclusive
                  onChange={(_, val) => val && setMobileView(val)}
                  size="small"
                >
                  <ToggleButton value="list">
                    <FormatListBulleted fontSize="small" sx={{ mr: 0.5 }} />
                    {intl.formatMessage({ id: "nutritionalTraining.listView" })}
                  </ToggleButton>
                  <ToggleButton value="chart">
                    <BarChart fontSize="small" sx={{ mr: 0.5 }} />
                    {intl.formatMessage({ id: "nutritionalTraining.chartView" })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            <Box display={isUpMD ? "flex" : "block"} gap={4}>
              {(isUpMD || mobileView === 'list') && (
                <Box flex="1" sx={{ minWidth: 0 }}>
                  {trainingRunsService.trainingRuns.map((trainingRun) => (
                    <TrainingRunItem key={trainingRun.id} trainingRun={trainingRun} />
                  ))}
                </Box>
              )}
              {(isUpMD || mobileView === 'chart') && (
                <Box flex="1" sx={{ minWidth: 0 }}>
                  <TrainingRunsChart trainingRuns={trainingRunsService.trainingRuns} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: "nutritionalTraining.customDateRange" })}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <DatePicker
                label={intl.formatMessage({ id: "nutritionalTraining.startDate" })}
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                maxDate={customEndDate || dayjs()}
              />
              <DatePicker
                label={intl.formatMessage({ id: "nutritionalTraining.endDate" })}
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                minDate={customStartDate || undefined}
                maxDate={customStartDate ? customStartDate.add(6, 'month') : dayjs()}
              />
              {customStartDate && customEndDate && customEndDate.diff(customStartDate, 'month', true) > 6 && (
                <Typography variant="caption" color="error">
                  {intl.formatMessage({ id: "nutritionalTraining.dateRangeTooLong" })}
                </Typography>
              )}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialogOpen(false)}>
            {intl.formatMessage({ id: "action.cancel" })}
          </Button>
          <Button
            onClick={handleCustomDateApply}
            disabled={!customStartDate || !customEndDate || customEndDate.diff(customStartDate, 'month', true) > 6}
            autoFocus
          >
            {intl.formatMessage({ id: "action.apply" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={trainingRunsService.isSaving}
        message={intl.formatMessage({ id: "data.saving" })}
        ContentProps={{
          sx: {
            background: "#ffffff",
            color: "#000000"
          }
        }}
      />

      <Snackbar
        open={trainingRunsService.hasSaveError}
        message={intl.formatMessage({ id: "data.savingError" })}
        autoHideDuration={6000}
        ContentProps={{
          sx: {
            background: "#e51f1f"
          }
        }}
      />
    </>
  );
});

export default NutritionalTraining;
